import { LockRounded, Notifications, NotificationsActive } from '@mui/icons-material';
import { Paper } from '@mui/material';
import { IconButton as IconButtonOutlined } from 'app/components';
import { LockOpenRoundedIcon } from 'app/icons';
import React, { forwardRef } from 'react';

import { GUARD_ACTIONS, GUARD_BIT_INDICES } from '../TabPanelCondition.constants';
import { GuardState } from '../TabPanelCondition.types';
import { alarmBlockStyle } from '../TabPanelConditionStyle';
import { UzaRelayControl } from './UzaRelayControl';

const IconButtonComponent = forwardRef((props, ref) => <div ref={ref} {...props} />);

/**
 * Секция с тревогой, УЗА реле и охраной
 *
 * @param {Object} transportItem - Данные транспорта
 * @param {Object} conditionItem - Текущее состояние
 * @param {boolean} galileoskyDevice - Есть ли устройство Galileosky
 * @param {Object} guardData - Данные охраны (биты guard_out, guard_status)
 * @param {boolean} guardPermission - Разрешение на управление охраной
 * @param {boolean} relayPermission - Разрешение на управление УЗА реле (суперадмин)
 * @param {Function} onGuardClick - Обработчик команд охраны
 * @param {Object} config - Конфигурация
 */
export const AlarmSection = ({
  transportItem,
  conditionItem,
  galileoskyDevice,
  guardData,
  guardPermission,
  relayPermission = false, // По умолчанию только суперадмин
  relayDisabled,
  onGuardClick,
  config,
}) => {
  const guardStatusAlarmBit = guardData.guardStatus[GUARD_BIT_INDICES.ALARM];
  const guardOutAlarmBit = guardData.guardOut[GUARD_BIT_INDICES.ALARM];

  const isGalileoskyDevice = galileoskyDevice && config.transportDeviceTypes[transportItem.device_type] === 'galileosky';

  const hasUzaRelay = !!transportItem.ground_sensor;

  return (
    <Paper variant='outlined' sx={alarmBlockStyle}>
      {/* Блок тревоги (Notifications) */}
      <div className='MuiPaperAlert-alarm'>
        <AlarmNotificationButton
          guardStatusBit={guardStatusAlarmBit}
          guardStatusNumber={guardData.guardStatusNumber}
          galileoskyDevice={galileoskyDevice}
          guardPermission={guardPermission}
          isGalileoskyDevice={isGalileoskyDevice}
          onAlarmClick={() => onGuardClick(GUARD_ACTIONS.ALARM_DISABLE)}
          config={config}
        />
      </div>

      {/* УЗА реле (пневмоклапан) */}
      {hasUzaRelay && (
        <div className='MuiPaperAlert-alarm'>
          <IconButtonComponent>
            <IconButtonOutlined size='medium' color='inherit' component='div' disabled={relayDisabled}>
              <UzaRelayControl
                uzaStatus={conditionItem.data?.uza_status ?? -1}
                disabled={relayDisabled}
                hasPermission={relayPermission}
                hasDevice={galileoskyDevice}
                onToggle={onGuardClick}
              />
            </IconButtonOutlined>
          </IconButtonComponent>
        </div>
      )}

      {/* Блок охраны (Lock) */}
      <div className='MuiPaperAlert-alarm'>
        <GuardLockButton
          guardOutBit={guardOutAlarmBit}
          guardOutNumber={guardData.guardOutNumber}
          galileoskyDevice={galileoskyDevice}
          guardPermission={guardPermission}
          isGalileoskyDevice={isGalileoskyDevice}
          onLockClick={(action) => onGuardClick(action)}
          config={config}
        />
      </div>
    </Paper>
  );
};

// Кнопка тревоги
const AlarmNotificationButton = ({
  guardStatusBit,
  guardStatusNumber,
  galileoskyDevice,
  guardPermission,
  isGalileoskyDevice,
  onAlarmClick,
  config,
}) => {
  if (!galileoskyDevice) {
    return (
      <IconButtonOutlined size='medium' color='inherit' disabled>
        <Notifications disabled />
      </IconButtonOutlined>
    );
  }

  if (guardStatusNumber === config.conditions.guard_status.default) {
    return (
      <IconButtonOutlined size='medium' color='inherit' disabled>
        <Notifications />
      </IconButtonOutlined>
    );
  }

  // guard_status[1] === '0' - нет тревоги
  if (guardStatusBit === GuardState.UNLOCKED) {
    return (
      <IconButtonComponent>
        <IconButtonOutlined size='medium' color='inherit' disabled>
          <Notifications />
        </IconButtonOutlined>
      </IconButtonComponent>
    );
  }

  // guard_status[1] === '1' - тревога активна
  return (
    <IconButtonComponent>
      <IconButtonOutlined size='medium' color='error' disabled={!guardPermission || !isGalileoskyDevice} onClick={onAlarmClick}>
        <NotificationsActive />
      </IconButtonOutlined>
    </IconButtonComponent>
  );
};

// Кнопка охраны
const GuardLockButton = ({ guardOutBit, guardOutNumber, galileoskyDevice, guardPermission, isGalileoskyDevice, onLockClick, config }) => {
  if (!galileoskyDevice) {
    return (
      <IconButtonOutlined size='medium' color='inherit' disabled>
        <LockRounded disabled />
      </IconButtonOutlined>
    );
  }

  if (guardOutNumber === config.conditions.guard_out.default) {
    return (
      <IconButtonOutlined size='medium' color='inherit' disabled>
        <LockRounded />
      </IconButtonOutlined>
    );
  }

  const isLocked = guardOutBit === GuardState.LOCKED;
  const Icon = isLocked ? LockRounded : LockOpenRoundedIcon;
  const color = isLocked ? 'primary' : 'inherit';
  const action = isLocked ? GUARD_ACTIONS.GUARD_LOCK : GUARD_ACTIONS.GUARD_UNLOCK;

  return (
    <IconButtonComponent>
      <IconButtonOutlined
        size='medium'
        color={color}
        disabled={!guardPermission || !isGalileoskyDevice}
        onClick={() => onLockClick(action)}>
        <Icon />
      </IconButtonOutlined>
    </IconButtonComponent>
  );
};
